import { useState } from 'react';
import { useSnapshot } from 'valtio';

import Tabs from '@cartwhl/ui/molecules/Tabs';

import { characterPickerStore, projectStore, userStore } from 'stores';
import { EXPORT_TYPES } from 'stores/animationGenerationStore';
import getCharacterDataFromProject from 'utils/getCharacterDataFromProject';

import { downloadTypeFromDefaultExportSettings } from '../utils';
import DownloadFile from './DownloadFile';
import DownloadVideo from './DownloadVideo';
import TabItem from './TabItem';

interface MotionDownloadProps {
  handleOpenChange: (open: boolean) => void;
}

export default function MotionDownload({
  handleOpenChange,
}: MotionDownloadProps) {
  const { currentProject } = useSnapshot(projectStore.state);
  const { characters } = useSnapshot(characterPickerStore.state);
  const characterData = getCharacterDataFromProject(currentProject?.data);
  // QPOS (MuJoCo joint vector) is only meaningful for MJCF-origin robots.
  const isMjcfCharacter =
    characters?.find((c) => c.PK === characterData?.characterID)
      ?.fileExtension === 'mjcf';

  const { userInfo } = useSnapshot(userStore.state);
  const accountDownload = downloadTypeFromDefaultExportSettings(
    userInfo.defaultExportSettings,
    { allowQpos: isMjcfCharacter },
  ) ?? { tab: EXPORT_TYPES.MP4, format: EXPORT_TYPES.MP4 };
  const [downloadFormat, setDownloadFormat] = useState(accountDownload.format);

  return (
    <Tabs
      defaultValue={accountDownload.tab}
      tabListClassName="-mx-4 lg:mx-0 px-4 lg:px-0"
      tabs={[
        {
          value: EXPORT_TYPES.MP4,
          label: <TabItem>Video</TabItem>,
          content: <DownloadVideo handleOpenChange={handleOpenChange} />,
        },
        {
          value: EXPORT_TYPES.BVH,
          label: <TabItem>BVH</TabItem>,
          content: (
            <DownloadFile
              tab={EXPORT_TYPES.BVH}
              downloadFormat={downloadFormat}
              setDownloadFormat={setDownloadFormat}
              handleOpenChange={handleOpenChange}
            />
          ),
        },
        {
          value: EXPORT_TYPES.FBX_MAYA,
          label: <TabItem>FBX</TabItem>,
          content: (
            <DownloadFile
              tab={EXPORT_TYPES.FBX_MAYA}
              downloadFormat={downloadFormat}
              setDownloadFormat={setDownloadFormat}
              handleOpenChange={handleOpenChange}
            />
          ),
        },
        {
          value: EXPORT_TYPES.GLB,
          label: <TabItem>glTF</TabItem>,
          content: (
            <DownloadFile
              tab={EXPORT_TYPES.GLB}
              downloadFormat={downloadFormat}
              setDownloadFormat={setDownloadFormat}
              handleOpenChange={handleOpenChange}
            />
          ),
        },
        {
          value: EXPORT_TYPES.USDZ,
          label: <TabItem>USD</TabItem>,
          content: (
            <DownloadFile
              tab={EXPORT_TYPES.USDZ}
              downloadFormat={downloadFormat}
              setDownloadFormat={setDownloadFormat}
              handleOpenChange={handleOpenChange}
            />
          ),
        },
        {
          value: EXPORT_TYPES.MA,
          label: <TabItem isPro>Maya</TabItem>,
          content: (
            <DownloadFile
              tab={EXPORT_TYPES.MA}
              downloadFormat={downloadFormat}
              setDownloadFormat={setDownloadFormat}
              handleOpenChange={handleOpenChange}
            />
          ),
        },
        ...(isMjcfCharacter
          ? [
              {
                value: EXPORT_TYPES.QPOS,
                label: <TabItem isPro>QPOS</TabItem>,
                content: (
                  <DownloadFile
                    tab={EXPORT_TYPES.QPOS}
                    downloadFormat={downloadFormat}
                    setDownloadFormat={setDownloadFormat}
                    handleOpenChange={handleOpenChange}
                  />
                ),
              },
            ]
          : []),
      ]}
    />
  );
}
